import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { getModuleDetailPage } from "../modules/utils/getModuleDetailPage";
import type { IntegrationModuleCardData } from "../types";
import { getStatusBadgeType, getStatusLabelKey } from "../utils";
import { resolveModuleName } from "../utils/resolve-module-name";
import { encodeSlug } from "../utils/slug";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import { ROUTES } from "@/constants/routes";

interface ModuleCardProps {
  module: IntegrationModuleCardData;
  onConfigure?: (moduleId: string) => void;
  moduleId?: string;
  integrationName?: string;
}

function ModuleCard({ module, onConfigure, integrationName }: ModuleCardProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { clientId } = useParams<{
    clientId?: string;
    integrationId?: string;
  }>();
  const { moduleName, error } = resolveModuleName(module.moduleName);
  if (error) {
    return null;
  }
  const isClickable =
    getModuleDetailPage(moduleName, Boolean(clientId)) !== null &&
    module.isActive;
  const statusBadgeType = getStatusBadgeType(module.isActive);
  const statusLabel = t(getStatusLabelKey(module.isActive));

  const handleConfigure = (e: React.MouseEvent) => {
    e.stopPropagation();
    onConfigure?.(module.id);
  };

  // Translate description if it's a translation key, otherwise use as-is
  const description =
    module.moduleDescription && i18n.exists(module.moduleDescription)
      ? t(module.moduleDescription)
      : module.moduleDescription || t("integrations.modules.descriptions.default");

  // Translate display name if it's a translation key, otherwise use as-is
  const displayName =
    module.moduleDisplayName && i18n.exists(module.moduleDisplayName)
      ? t(module.moduleDisplayName)
      : module.moduleDisplayName;

  const viewOverview = () => {
    if (!integrationName) {
      return null;
    }
    const encodedIntegrationName = encodeSlug(integrationName);
    if (clientId) {
      return `/${ROUTES.clients}/${clientId}/${ROUTES.integrations}/${encodedIntegrationName}/${moduleName}`;
    }
    return `/${ROUTES.integrations}/${encodedIntegrationName}/${moduleName}`;
  };
  return (
    <Card
      hover={isClickable}
      className={`flex flex-col justify-between ${
        isClickable ? "cursor-pointer" : ""
      }`}
      onClick={() => {
        if (!isClickable) {
          return;
        }
        const href = viewOverview();
        if (!href) {
          return;
        }
        navigate(href);
      }}
    >
      <CardHeader className="flex flex-col gap-2">
        <CardTitle className="text-base font-semibold leading-6">
          {displayName}
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>

      <CardFooter className="flex flex-row-reverse items-center justify-between border-t border-border">
        <div>
          <Badge variant={statusBadgeType} size="medium">
            {statusLabel}
          </Badge>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleConfigure}>
            {t("common.configure", "Configure")}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}

export default ModuleCard;
